import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Building2, Loader2, FolderOpen, Plus, X, Search, ArrowUp, ArrowDown, LockKeyhole } from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import { projectAuditCount, projectLastAudit } from '../lib/projectAuditHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [dbProjects, setDbProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newOriginator, setNewOriginator] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'name'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const projResp = await apiFetch(`${API_URL}/api/projects`);
      if (projResp.ok) {
        const pj = await projResp.json();
        setDbProjects((pj.projects || []).filter(p => p.code !== 'DEFAULT'));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createProject = async () => {
    if (!newName.trim() || !newCode.trim()) {
      setError('Name and code are required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const resp = await apiFetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName.trim(),
          code: newCode.trim().toUpperCase(),
          originator: newOriginator.trim().toUpperCase(),
          description: '',
        }),
      });
      if (!resp.ok) {
        let detail = 'Failed to create project';
        try {
          const errBody = await resp.json();
          if (errBody?.detail) detail = errBody.detail;
        } catch { /* non-JSON error body, keep generic message */ }
        throw new Error(detail);
      }
      const proj = await resp.json();
      setDbProjects(prev => [...prev, proj]);
      setShowCreate(false);
      setNewName(''); setNewCode(''); setNewOriginator('');
    } catch (err) { setError(err.message); }
    finally { setCreating(false); }
  };

  // All projects come from the database. No hardcoded fixtures.
  const allProjects = dbProjects.map(p => ({
    id: p.id,
    dbId: p.id,
    name: p.name,
    code: p.code,
    originator: p.originator || '',
    created_at: p.created_at || null,
    audit_count: p.audit_count,
    last_audit: p.last_audit || null,
    is_shared_demo: Boolean(p.is_shared_demo),
    is_read_only: Boolean(p.is_read_only),
  }));

  const q = search.trim().toLowerCase();
  const filteredProjects = q
    ? allProjects.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q) ||
        (p.originator || '').toLowerCase().includes(q)
      )
    : allProjects;

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'name') {
      cmp = (a.name || '').localeCompare(b.name || '');
    } else {
      const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
      cmp = ad - bd;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  if (loading) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-accent" /></div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="projects-title">
              Projects
              <span className="ml-3 text-lg font-medium text-muted-foreground align-middle" data-testid="projects-count">
                ({sortedProjects.length}{q && sortedProjects.length !== allProjects.length ? ` of ${allProjects.length}` : ''})
              </span>
            </h1>
            <p className="text-muted-foreground">Manage your BIM projects and their associated documents</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} data-testid="new-project-btn">
            <Plus className="h-4 w-4 mr-2" /> New Project
          </Button>
        </div>

        {/* Search + Sort Controls */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search projects by name, code or originator..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="projects-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="projects-sort-by"
              aria-label="Sort by"
            >
              <option value="date">Sort by date</option>
              <option value="name">Sort by name</option>
            </select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
              data-testid="projects-sort-dir"
            >
              {sortDir === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Create Project Form */}
        {showCreate && (
          <Card className="mb-6 border-accent">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Create New Project</CardTitle>
              <CardDescription>Add a new building project. Upload its BEP in the next step.</CardDescription>
            </CardHeader>
            <CardContent>
              {error && <p className="text-sm text-[hsl(var(--destructive))] mb-3">{error}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Project Name *</label>
                  <Input placeholder="e.g. Tower A" value={newName} onChange={e => setNewName(e.target.value)}
                    data-testid="new-project-name" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Project Code *</label>
                  <Input placeholder="e.g. TWA" value={newCode} onChange={e => setNewCode(e.target.value)}
                    className="uppercase" data-testid="new-project-code" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Originator Code</label>
                  <Input placeholder="e.g. ABC" value={newOriginator} onChange={e => setNewOriginator(e.target.value)}
                    className="uppercase" data-testid="new-project-originator" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={createProject} disabled={creating} data-testid="create-project-submit">
                  {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create Project
                </Button>
                <Button variant="outline" onClick={() => { setShowCreate(false); setError(''); }}>
                  <X className="h-4 w-4 mr-2" /> Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project List */}
        <div className="space-y-3">
          {sortedProjects.length === 0 && (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed rounded-md"
                 data-testid="projects-empty-state">
              {q ? 'No projects match your search.' : 'No projects yet. Create your first project.'}
            </div>
          )}
          {sortedProjects.map((project, idx) => {
            const auditCount = projectAuditCount(project);
            const lastReview = projectLastAudit(project);
            const detailPath = `/projects/${project.dbId || project.id}`;

            return (
              <Card key={project.id}
                className="transition-colors cursor-pointer hover:border-accent/50"
                role="link"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(detailPath);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(detailPath);
                  }
                }}
                data-testid={`project-card-${project.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-md bg-muted text-sm font-semibold text-muted-foreground tabular-nums"
                         data-testid={`project-index-${idx + 1}`}>
                      {idx + 1}
                    </div>
                    <div className="p-3 rounded-lg bg-accent/10">
                      <Building2 className="h-6 w-6 text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-foreground">{project.name}</h3>
                        <Badge variant="outline" className="text-xs">{project.code}{project.originator ? `-${project.originator}` : ''}</Badge>
                        {project.is_shared_demo ? (
                          <Badge
                            variant="outline"
                            className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                            data-testid={`project-demo-access-${project.id}`}
                          >
                            <LockKeyhole className="h-3 w-3" />
                            {project.is_read_only ? 'Demo · Read-only' : 'Demo · Owner'}
                          </Badge>
                        ) : (
                          <Badge className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30 text-xs">Active</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1" data-testid={`project-audit-count-${project.id}`}>
                          <FolderOpen className="h-3 w-3" /> {auditCount == null ? '—' : auditCount} audits
                        </span>
                        {lastReview && <span>Last review: {lastReview.toLocaleDateString()}</span>}
                        {project.created_at && <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
